import {
  Wrench,
  Television,
  Broadcast,
  Desktop,
  CellTower,
  VideoCamera,
  ArrowUpRight,
  CheckCircle,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";

const ServiceCard = ({
  testId,
  slug,
  colSpan,
  Icon,
  title,
  desc,
  items,
  accent,
  bgImage,
}) => (
  <Link
    to={`/servicos/${slug}`}
    data-testid={testId}
    className={`bento-card hover-lift group relative block overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-sm ${colSpan}`}
  >
    {bgImage && (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-[0.06] transition-opacity duration-500 group-hover:opacity-[0.12]"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
    )}
    <div
      aria-hidden
      className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full blur-3xl transition-opacity duration-500 group-hover:opacity-90"
      style={{ background: accent }}
    />

    <div className="relative z-10 flex h-full flex-col">
      <div className="flex items-start justify-between">
        <div className="icon-tile">
          <Icon size={24} weight="duotone" />
        </div>
        <ArrowUpRight
          size={22}
          weight="bold"
          className="text-slate-400 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-blue-600"
        />
      </div>

      <h3 className="font-heading mt-8 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        {title}
      </h3>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
        {desc}
      </p>

      <ul className="mt-6 space-y-2.5">
        {items.map((it) => (
          <li
            key={it}
            className="flex items-start gap-2.5 text-sm text-slate-700"
          >
            <CheckCircle
              size={16}
              weight="fill"
              className="mt-0.5 shrink-0 text-blue-600"
            />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  </Link>
);

export const Services = () => {
  const { t } = useLang();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative scroll-mt-[160px] md:scroll-mt-[210px] lg:scroll-mt-[240px] border-t border-slate-200 py-24 lg:py-32"
    >
      {/* Background photo: technical workshop */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img
          src="/sections/services-bg.png"
          alt=""
          aria-hidden
          loading="lazy"
          className="h-full w-full object-cover object-center"
          style={{ filter: "saturate(1.15) contrast(1.05)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#d6e6f2]/15 via-[#cfe0ed]/25 to-[#a8c8e2]/40" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
              {t.services.overline}
            </div>
            <h2
              data-testid="services-title"
              className="font-heading mt-5 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
            >
              {t.services.title}
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-slate-600">
            {t.services.subtitle}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
          <ServiceCard
            testId="service-assistance"
            slug="assistencia-tecnica"
            colSpan="md:col-span-8"
            Icon={Wrench}
            title={t.services.assistance.title}
            desc={t.services.assistance.desc}
            items={t.services.assistance.items}
            accent="rgba(37,99,235,0.15)"
          />
          <ServiceCard
            testId="service-television"
            slug="televisao"
            colSpan="md:col-span-4"
            Icon={Television}
            title={t.services.television.title}
            desc={t.services.television.desc}
            items={t.services.television.items}
            accent="rgba(168,85,247,0.15)"
            bgImage="https://images.unsplash.com/photo-1734126650058-623935bab140?crop=entropy&cs=srgb&fm=jpg&w=900&q=75"
          />
          <ServiceCard
            testId="service-communications"
            slug="comunicacoes"
            colSpan="md:col-span-4"
            Icon={Broadcast}
            title={t.services.communications.title}
            desc={t.services.communications.desc}
            items={t.services.communications.items}
            accent="rgba(220,38,38,0.1)"
          />
          <ServiceCard
            testId="service-it"
            slug="informatica"
            colSpan="md:col-span-8"
            Icon={Desktop}
            title={t.services.it.title}
            desc={t.services.it.desc}
            items={t.services.it.items}
            accent="rgba(59,130,246,0.15)"
            bgImage="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&w=1200&q=75"
          />
          <ServiceCard
            testId="service-towers"
            slug="mastros-torres"
            colSpan="md:col-span-6"
            Icon={CellTower}
            title={t.services.towers.title}
            desc={t.services.towers.desc}
            items={t.services.towers.items}
            accent="rgba(16,185,129,0.15)"
          />
          <ServiceCard
            testId="service-security"
            slug="seguranca-cctv"
            colSpan="md:col-span-6"
            Icon={VideoCamera}
            title={t.services.security.title}
            desc={t.services.security.desc}
            items={t.services.security.items}
            accent="rgba(244,63,94,0.12)"
          />
        </div>
      </div>
    </section>
  );
};
